import { HeavenStem, EarthBranch } from 'tyme4ts';
import { Element } from './elements';

// ========== 关系结构模型相关工具函数 ==========

/**
 * 关系结构模型标准配置（中州派 + 三合派）
 */
export const ZIWEI_CONFIG = {
  school: 'sanhe', // 使用三合派系统
  tianma: 'fromMonthly', // 天马：依据月支
  kongxing: 'regular', // 天空：常规排法
  brightness: 'zhongzhou', // 星耀亮度：中州派理论
  jiekong: 'duoble', // 截空旬空：正负双星法
  kuiyue: 'liuxinma', // 魁钺：六辛逢马虎
  mingzhu: 'zhongzhou', // 命主：中州派理论
  changsheng12: 'yinyang' // 长生十二神：区分阴阳顺逆
} as const;

/**
 * 计算身宫位置
 * @param birthTime 时辰索引 (0-12)
 * @returns 身宫名称
 */
export const getBodyPalace = (birthTime: number): string => {
  const timeToBodyPalace: { [key: number]: string } = {
    0: '命宫',   // 子时
    1: '福德',   // 丑时
    2: '官禄',   // 寅时
    3: '迁移',   // 卯时
    4: '财帛',   // 辰时
    5: '夫妻',   // 巳时
    6: '命宫',   // 午时
    7: '福德',   // 未时
    8: '官禄',   // 申时
    9: '迁移',   // 酉时
    10: '财帛',  // 戌时
    11: '夫妻'   // 亥时
  };
  return timeToBodyPalace[birthTime] || '命宫';
};

/**
 * 查找星曜所在的宫位
 * @param starName 星曜名称（如"廉贞"、"天同"）
 * @param palaces 本分析图谱十二宫数组
 * @returns 宫位名称，未找到返回 null
 */
export const findStarPalace = (starName: string, palaces: any[]): string | null => {
  for (const palace of palaces) {
    // 在主星中查找
    const foundInMajor = palace.majorStars?.find((s: any) => s.name === starName);
    if (foundInMajor) return palace.name;

    // 在辅星中查找
    const foundInMinor = palace.minorStars?.find((s: any) => s.name === starName);
    if (foundInMinor) return palace.name;
  }
  return null;
};

/**
 * 获取四化含义解读
 * @param type 四化类型（禄/权/科/忌）
 * @param star 星曜名称
 * @param palace 宫位名称
 * @returns 中文解读
 */
export const getMutationMeaning = (type: '禄' | '权' | '科' | '忌', star: string, palace: string | null): string => {
  const palaceName = palace || '未知宫';

  // 四化基本含义
  const typeMeanings: Record<string, string> = {
    '禄': '福气、财禄、顺遂',
    '权': '权力、掌控、变动',
    '科': '名声、贵人、科甲',
    '忌': '执着、困扰、阻碍'
  };

  // 宫位基本含义
  const palaceMeanings: Record<string, string> = {
    '命宫': '自我、性格、发展走向',
    '兄弟': '兄弟姐妹、人际关系',
    '夫妻': '婚姻、感情、配偶',
    '子女': '子女、晚辈、创作',
    '财帛': '财富、收入、理财',
    '疾厄': '健康、疾病、体质',
    '迁移': '外出、变动、机遇',
    '仆役': '朋友、同事、下属',
    '官禄': '事业、工作、学业',
    '田宅': '房产、家庭、根基',
    '福德': '精神、享受、福报',
    '父母': '父母、长辈、文书'
  };

  const typeDesc = typeMeanings[type] || type;
  const palaceDesc = palaceMeanings[palaceName] || palaceName;

  if (type === '忌') {
    return `${star}在${palaceName}（${palaceDesc}）有${typeDesc}之象，需留意${palaceName}相关事务的波动`;
  } else if (type === '禄') {
    return `${star}在${palaceName}（${palaceDesc}）带来${typeDesc}，是${palaceName}的吉象`;
  } else if (type === '权') {
    return `${star}在${palaceName}（${palaceDesc}）主${typeDesc}，${palaceName}有掌控欲或变动`;
  } else {
    return `${star}在${palaceName}（${palaceDesc}）主${typeDesc}，${palaceName}有贵人或名声`;
  }
};

/**
 * 计算来因宫
 * @param astrolabe iztro 返回的分析图谱对象
 * @returns 来因宫信息
 */
export const getLaiYinGong = (astrolabe: any): { name: string; heavenlyStem: string; earthlyBranch: string } | null => {
  if (!astrolabe || !astrolabe.chineseDate) return null;

  // 从 chineseDate 提取生年天干
  // 格式为 "丙午 庚寅..."，取第 0 位
  const birthYearStem = astrolabe.chineseDate.charAt(0);

  // 遍历 palaces 数组找到天干匹配的宫位
  const laiYinPalace = astrolabe.palaces.find(
    (palace: any) => palace.heavenlyStem === birthYearStem
  );

  return laiYinPalace ? {
    name: laiYinPalace.name,
    heavenlyStem: laiYinPalace.heavenlyStem,
    earthlyBranch: laiYinPalace.earthlyBranch
  } : null;
};

/**
 * 构建关系结构模型数据（统一格式）
 * @param astrolabe iztro 返回的分析图谱对象
 * @param birthTime 时辰索引 (0-12)
 * @returns 标准化的关系结构数据
 */
export const buildZiWeiData = (astrolabe: any, birthTime: number, targetYears?: number | number[]) => {
  if (!astrolabe) return null;

  try {
    // 基础分析图谱信息
    const data: any = {
      // 分析图谱基本信息
      basic: {
        soul: astrolabe.soul,           // 命主
        body: astrolabe.body,           // 身主
        fiveElementsClass: astrolabe.fiveElementsClass,  // 五行局
        lunarDate: astrolabe.lunarDate, // 农历生日
        chineseDate: astrolabe.chineseDate // 干支历
      },
      // 十二宫详细数据
      palaces: [] as any[],
      // 大限信息
      decadals: [] as any[],
      // 年度趋势信息
      yearly: [] as any[]
    };

    // 计算年度趋势数据（支持单个年份或年份数组）
    if (targetYears && astrolabe.horoscope) {
      const years = Array.isArray(targetYears) ? targetYears : [targetYears];
      data.yearly = [];

      for (const year of years) {
        try {
          // 使用 horoscope 方法计算年度趋势 (参数: 目标日期字符串, 目标时辰索引)
          // 日期格式: YYYY-MM-DD
          const targetDate = `${year}-05-01`;
          const horoscope = astrolabe.horoscope(targetDate, 0);
          if (horoscope && horoscope.yearly) {
            const yearlyData = horoscope.yearly;
            data.yearly.push({
              year,
              index: yearlyData.index,
              ganZhi: `${yearlyData.heavenlyStem}${yearlyData.earthlyBranch}`,
              name: yearlyData.name,
              mutations: yearlyData.mutagen,
              palaceNames: yearlyData.palaceNames,
              stars: yearlyData.stars,
              palaces: horoscope.palaces?.map((palace: any) => ({
                name: palace.name,
                index: palace.index,
                heavenlyStem: palace.heavenlyStem,
                earthlyBranch: palace.earthlyBranch,
                majorStars: palace.majorStars?.map((s: any) => ({
                  name: s.name,
                  type: s.type,
                  brightness: s.brightness,
                  mutagen: s.mutagen
                })) || [],
                minorStars: palace.minorStars?.map((s: any) => ({
                  name: s.name,
                  type: s.type,
                  brightness: s.brightness,
                  mutagen: s.mutagen
                })) || []
              })) || []
            });
          }
        } catch (yearError) {
          console.error(`年度趋势 ${year} 计算失败:`, yearError);
        }
      }

      // 如果只传了一个年份，保持原来的单对象格式兼容
      if (data.yearly.length === 1) {
        data.yearly = data.yearly[0];
      }
    }

    // 计算身宫位置
    const bodyPalaceName = getBodyPalace(birthTime);
    data.basic.bodyPalace = bodyPalaceName;

    // 计算来因宫
    const laiYinPalace = getLaiYinGong(astrolabe);
    if (laiYinPalace) {
      data.basic.laiYinGong = laiYinPalace;
    }

    // 收集十二宫信息
    astrolabe.palaces.forEach((palace: any) => {
      const palaceData: any = {
        name: palace.name,
        heavenlyStem: palace.heavenlyStem,
        earthlyBranch: palace.earthlyBranch,
        majorStars: palace.majorStars.map((s: any) => ({
          name: s.name,
          type: s.type,
          brightness: s.brightness,
          mutagen: s.mutagen // 获取四化信息（禄、权、科、忌）
        })),
        minorStars: palace.minorStars.map((s: any) => ({
          name: s.name,
          type: s.type,
          brightness: s.brightness,
          mutagen: s.mutagen // 辅星也可能有四化
        })),
        adjectiveStars: palace.adjectiveStars?.map((s: any) => ({
          name: s.name,
          type: s.type
        })) || []
      };

      // 标记是否为身宫
      if (palace.name === bodyPalaceName) {
        palaceData.isBodyPalace = true;
      }

      // 标记是否为来因宫
      if (laiYinPalace && palace.name === laiYinPalace.name) {
        palaceData.isLaiYinGong = true;
      }

      data.palaces.push(palaceData);

      // 收集大限信息
      if (palace.decadal) {
        data.decadals.push({
          palace: palace.name,
          heavenlyStem: palace.heavenlyStem,
          earthlyBranch: palace.earthlyBranch,
          startAge: palace.decadal.range[0],
          endAge: palace.decadal.range[1]
        });
      }
    });

    // 大限按起始年龄排序，并添加中文描述
    data.decadals.sort((a: any, b: any) => a.startAge - b.startAge);
    data.decadals = data.decadals.map((d: any) => ({
      ...d,
      text: `${d.startAge}-${d.endAge}岁 ${d.heavenlyStem}${d.earthlyBranch} ${d.palace}宫`
    }));

    // 为每个年度趋势计算四化落宫（需要在 data.palaces 填充完成后执行）
    if (data.yearly && data.palaces.length > 0) {
      const yearlyArray = Array.isArray(data.yearly) ? data.yearly : [data.yearly];
      data.yearly = yearlyArray.map((yearly: any) => {
        if (!yearly.mutations) return yearly;

        const mutations = yearly.mutations;
        const luPalace = findStarPalace(mutations[0], data.palaces);
        const quanPalace = findStarPalace(mutations[1], data.palaces);
        const kePalace = findStarPalace(mutations[2], data.palaces);
        const jiPalace = findStarPalace(mutations[3], data.palaces);

        return {
          // 年度趋势基本信息
          year: yearly.year,
          ganZhi: yearly.ganZhi,
          // 年度趋势命宫位置
          lifePalace: {
            index: yearly.index,
            name: yearly.palaceNames?.[yearly.index] || '未知'
          },
          // 四化飞星
          mutations: {
            lu: { star: mutations[0], palace: luPalace, text: `${mutations[0]}化禄在${luPalace || '未知宫'}` },
            quan: { star: mutations[1], palace: quanPalace, text: `${mutations[1]}化权在${quanPalace || '未知宫'}` },
            ke: { star: mutations[2], palace: kePalace, text: `${mutations[2]}化科在${kePalace || '未知宫'}` },
            ji: { star: mutations[3], palace: jiPalace, text: `${mutations[3]}化忌在${jiPalace || '未知宫'}` }
          },
          // 四化文本列表（便于直接使用）
          mutationList: [
            `${mutations[0]}化禄在${luPalace || '未知宫'}`,
            `${mutations[1]}化权在${quanPalace || '未知宫'}`,
            `${mutations[2]}化科在${kePalace || '未知宫'}`,
            `${mutations[3]}化忌在${jiPalace || '未知宫'}`
          ]
        };
      });
    }

    return data;
  } catch (error) {
    console.error('构建关系结构模型数据失败', error);
    return null;
  }
};

// ========== 原有导出 ==========

export const heavenStems = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
export const earthBranches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];

export const getElement = (pillar: string):Element => {
  if (heavenStems.includes(pillar)) {
    const heavenStem = HeavenStem.fromName(pillar);
    return heavenStem.getElement().getName() as unknown as Element;
  } else if (earthBranches.includes(pillar)) {
    const earthBranch = EarthBranch.fromName(pillar);
    return earthBranch.getElement().getName() as unknown as Element;
  } else {
    throw new Error('Invalid pillar: ' + pillar);
  }
}

import { SolarDay, SolarTime, LunarYear } from 'tyme4ts';
import { Solar as LunarSolar, Lunar } from 'lunar-typescript';

export function calculateBirthTime(
  inputYear: string,
  inputMonth: string,
  inputDay: string,
  inputHour: string
): {solar: string, lunar: string}[] {
  // if (!inputYear || !inputMonth || !inputDay || !inputHour) {
  //   throw new Error('Invalid input');
  // }

  const results: Array<{solar: string, lunar: string}> = [];

  for (let solarYear = 1900; solarYear <= 2100; solarYear++) {
    const lunarYear = LunarYear.fromYear(solarYear);
    if (lunarYear.getSixtyCycle().getName() === inputYear) {
      // 年份匹配，检查这一年的每一天
      for (let month = 1; month <= 12; month++) {
        for (let day = 1; day <= 31; day++) {
          try {
            const solarDay = SolarDay.fromYmd(solarYear, month, day);
            const lunarDay = solarDay.getLunarDay();

            const sixtyCycleMonth = lunarDay.getMonthSixtyCycle().getName()
            const sixtyCycleDay = lunarDay.getSixtyCycle().getName()

            // 检查月柱和日柱是否同时匹配
            if (sixtyCycleMonth === inputMonth && sixtyCycleDay === inputDay) {

              if(inputHour){
              // 找到匹配的日期，现在查找匹配的小时
              for (let h = 0; h < 24; h++) {
                const solarTime = SolarTime.fromYmdHms(solarYear, month, day, h, 0, 0);
                if (solarTime.getLunarHour().getSixtyCycle().getName() === inputHour) {
                  results.push({
                    solar: `${solarYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${h.toString().padStart(2, '0')}:00:00`,
                    lunar: `${lunarYear.getYear()}年${lunarYear.getLeapMonth() !== 0 ? '闰' : ''}${lunarDay.getLunarMonth()}${lunarDay.getDay()}日${solarTime.getLunarHour()}`
                  });
                }
              }
              }else{
                const h = 0;
                results.push({
                  solar: `${solarYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${h.toString().padStart(2, '0')}:00:00`,
                  lunar: `${lunarYear.getYear()}年${lunarYear.getLeapMonth() ? '闰' : ''}${lunarDay.getLunarMonth()}${lunarDay.getDay()}日`
                });
              }

            }
          } catch {
            // 无效日期，继续下一天
          }
        }
      }
    }
  }

  if (results.length === 0) {
    throw new Error('Cannot find matching dates within the year range 1900-2100');
  }

  return results;
}

export type Gender = 'male' | 'female';

export interface DaYun {
  index: number;
  ganZhi: string;
}

function nextIndex(index: number, length: number, forward: boolean): number {
  return (index + (forward ? 1 : -1) + length) % length;
}

export function calculateDaYunPillars(yearPillar: string, monthPillar: string, gender: Gender, count: number = 8): DaYun[] {
  const yangStems: string[] = ['甲', '丙', '戊', '庚', '壬'];
  const yearStem = yearPillar[0];
  const isYangYear = yangStems.includes(yearStem);
  const isForward = (isYangYear && gender === 'male') || (!isYangYear && gender === 'female');

  const monthStem = monthPillar[0];
  const monthBranch = monthPillar[1];

  let stemIndex = heavenStems.indexOf(monthStem);
  let branchIndex = earthBranches.indexOf(monthBranch);

  const result: DaYun[] = [];

  for (let i = 0; i < count; i++) {
    stemIndex = nextIndex(stemIndex, heavenStems.length, isForward);
    branchIndex = nextIndex(branchIndex, earthBranches.length, isForward);

    result.push({
      index: i + 1,
      ganZhi: heavenStems[stemIndex] + earthBranches[branchIndex],
    });
  }

  return result;
}

export interface DaYunWithAge extends DaYun {
  startAge: number;
  endAge: number;
  startYear: number;
  endYear: number;
}

export interface QiYunDetail {
  startAge: number;           // 起运年龄（岁）
  startYear: number;          // 起运年份
  startMonth: number;         // 起运月份（大约）
  startDay: number;           // 起运日（大约）
  direction: 'forward' | 'backward';  // 顺推还是逆推
  isYangYear: boolean;        // 是否阳年
}

export function calculateQiYunStartAge(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  gender: Gender,
): number {
  const birthSolar = LunarSolar.fromYmdHms(year, month, day, hour, minute, 0);
  const lunar = Lunar.fromSolar(birthSolar);
  const eightChar = lunar.getEightChar();
  const genderNum = gender === 'male' ? 1 : 0;
  // 使用 lunar-typescript 内置的 sect=2 起运算法（三天折一年等）
  const yun = eightChar.getYun(genderNum, 2);
  return yun.getStartYear();
}

// 获取详细的起运信息
export function calculateQiYunDetail(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  gender: Gender,
): QiYunDetail {
  const birthSolar = LunarSolar.fromYmdHms(year, month, day, hour, minute, 0);
  const lunar = Lunar.fromSolar(birthSolar);
  const eightChar = lunar.getEightChar();
  const genderNum = gender === 'male' ? 1 : 0;
  const yun = eightChar.getYun(genderNum, 2);
  
  // 获取精确的起运日期
  const startSolar = yun.getStartSolar();
  if (!startSolar) {
    // 如果没有起运日期，使用整数年龄
    const startAgeInt = yun.getStartYear();
    return {
      startAge: startAgeInt,
      startYear: year + startAgeInt,
      startMonth: 0,
      startDay: 0,
      direction: 'forward',
      isYangYear: false,
    };
  }
  
  // 计算起运年龄（精确到月日）
  const startYear = startSolar.getYear();
  const startMonth = startSolar.getMonth();
  const startDay = startSolar.getDay();
  
  // 计算年龄差（岁、月、日）
  let ageYears = startYear - year;
  let ageMonths = startMonth - month;
  let ageDays = startDay - day;
  
  // 调整负数
  if (ageDays < 0) {
    ageMonths--;
    ageDays += 30; // 简化处理，每月30天
  }
  if (ageMonths < 0) {
    ageYears--;
    ageMonths += 12;
  }
  
  // 判断年份阴阳
  const yearStem = eightChar.getYearGan();
  const yangStems = ['甲', '丙', '戊', '庚', '壬'];
  const isYangYear = yangStems.includes(yearStem);
  
  // 判断方向
  const direction: 'forward' | 'backward' = 
    (isYangYear && gender === 'male') || (!isYangYear && gender === 'female') 
      ? 'forward' 
      : 'backward';
  
  return {
    startAge: ageYears,
    startYear,
    startMonth: ageMonths,
    startDay: ageDays,
    direction,
    isYangYear,
  };
}

export function calculateDaYunWithAge(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  yearPillar: string,
  monthPillar: string,
  gender: Gender,
  count: number = 8,
): DaYunWithAge[] {
  // 使用详细起运信息获取准确的起运年份
  const qiYunDetail = calculateQiYunDetail(year, month, day, hour, minute, gender);
  const firstAge = qiYunDetail.startAge;
  const firstYear = qiYunDetail.startYear;

  const pillars = calculateDaYunPillars(yearPillar, monthPillar, gender, count);

  return pillars.map((p, idx) => {
    const startAge = firstAge + idx * 10;
    const endAge = firstAge + (idx + 1) * 10;
    const startYear = firstYear + idx * 10;
    const endYear = firstYear + (idx + 1) * 10;
    return {
      ...p,
      startAge,
      endAge,
      startYear,
      endYear,
    };
  });
}

// ========== 微信浏览器检测 ==========

/**
 * 检测是否在微信内置浏览器中打开
 * @returns boolean 是否在微信浏览器中
 */
export function isWeChatBrowser(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /micromessenger/.test(ua);
}
